import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';

import { CheckEmailResponse, CreatePasswordRequest, LoginRequest, LoginResponse, OTPRequest, VerifyOTPRequest } from '../../models/common/login';
import { RegisterRequest, TokenRequest } from '../../models/common/register';
import { OrgConfigs } from '../../models/common/org-configs';
import { ActiveShow } from '../../models/common/active-show';
import { SidebarProfile } from '../../models/common/sidebar';
import { GetRolesRequest, GetRolesResponse } from '../../models/common/role';
import {
  GetShowsRequest,
  GetShowsResponse,
  GetShowTypesResponse
} from '../../models/common/show-api';
import { GetCountiesResponse } from '../../models/common/family-profile';
import { EducatorShowClubMappingRequest } from '../../models/common/educator-lookup';
import { StateAdminShowValidationBody } from '../../models/common/show-admin-lookup';
import {
  CHECK_EMAIL,
  CREATE_PASSWORD,
  SAVE_FAMILY_DETAILS,
  GET_ORG_CONFIGS,
  GET_ROLES,
  GET_SHOWS,
  GET_SHOW_TYPES,
  GET_USER_INFO,
  GET_COUNTIES,
  VALIDATE_EDUCATOR_SHOW_CLUB_MAPPING,
  LOGIN,
  LOGIN_OTP,
  LOGIN_OTP_REQUEST,
  REQUEST_OTP,
  VALIDATE_FAMILY_REGISTRATION,
  VALIDATE_STATE_ADMIN_SHOW,
  VERIFY_OTP_REQUEST
} from '../constants/api-endpoints';

export type { ActiveShow };

/** Merged core service: family-portal + active-show + org-config + new APIs */
@Injectable({ providedIn: 'root' })
export class CoreService {
  static readonly KEY_FAMILY_ID = 'user-family-id';
  static readonly KEY_FAMILY_FOURH_COUNTY = 'user-family-fourh-county';
  static readonly KEY_CLUB_ID = 'club-id';
  private readonly ORG_CONFIG_KEY = 'org-config';

  private readonly _activeShow$ = new BehaviorSubject<ActiveShow | null>(null);
  readonly activeShow$ = this._activeShow$.asObservable();

  private readonly _headerProfile$ = new BehaviorSubject<SidebarProfile | null>(null);
  readonly headerProfile$ = this._headerProfile$.asObservable();

  private readonly _showHeaderLogout$ = new BehaviorSubject<boolean>(false);
  readonly showHeaderLogout$ = this._showHeaderLogout$.asObservable();

  constructor(private readonly http: HttpClient) {}

  // ===================== Active Show Methods =====================

  setActiveShow(show: ActiveShow): void {
    this._activeShow$.next(show);
  }

  clearActiveShow(): void {
    this._activeShow$.next(null);
  }

  // ===================== Header Profile Methods =====================

  setHeaderProfile(profile: SidebarProfile | null): void {
    this._headerProfile$.next(profile);
  }

  clearHeaderProfile(): void {
    this._headerProfile$.next(null);
  }

  setShowHeaderLogout(show: boolean): void {
    this._showHeaderLogout$.next(show);
  }

  // ===================== Family Portal Auth Methods =====================

  setClubName(fourHClubName: string | undefined): void {
    if (fourHClubName) {
      sessionStorage.setItem(CoreService.KEY_FAMILY_FOURH_COUNTY, fourHClubName);
    } else {
      sessionStorage.removeItem(CoreService.KEY_FAMILY_FOURH_COUNTY);
    }
  }

  checkEmail(email: string): Observable<CheckEmailResponse> {
    return this.http.get<CheckEmailResponse>(`${CHECK_EMAIL}?email=${encodeURIComponent(email)}`);
  }

  /** Family registration via the dynamic SaveFamilyDetails API. Anonymous: the
   *  family has no token yet, and the API resolves the family role itself. */
  register(payload: RegisterRequest, show: string = 'tsp'): Observable<unknown> {
    return this.http.post<unknown>(SAVE_FAMILY_DETAILS(show), payload);
  }

  validateToken(payload: TokenRequest, show: string = 'tsp'): Observable<unknown> {
    return this.http.post<unknown>(VALIDATE_FAMILY_REGISTRATION(show), payload);
  }

  /** Validate email token with Authorization header for authenticated endpoint.
   * The token from the email link is used as the Bearer token for authentication.
   */
  validateTokenWithAuth(payload: TokenRequest, token: string, show: string = 'tsp'): Observable<unknown> {
    // Send the email verification token as Bearer token
    // This overrides any session token from AuthInterceptor
    const headers = {
      'Authorization': `Bearer ${token}`
    };

    console.log('Validating email token with Bearer auth');
    console.log('Endpoint:', VALIDATE_FAMILY_REGISTRATION(show));
    console.log('Token (first 20 chars):', token.substring(0, 20) + '...');

    return this.http.post<unknown>(VALIDATE_FAMILY_REGISTRATION(show), payload, { headers });
  }

  requestOTP(email: string): Observable<any> {
    const headers = { 'Content-Type': 'text/json' };
    return this.http.post<any>(REQUEST_OTP, JSON.stringify(email), { headers });
  }

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(LOGIN, payload);
  }

  loginOTP(payload: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(LOGIN_OTP, payload);
  }

  createPassword(payload: CreatePasswordRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(CREATE_PASSWORD, payload);
  }

  // ===================== New OTP APIs with roleId =====================

  /** Request OTP with roleId (new API) */
  requestOTPWithRole(payload: OTPRequest,show: string = 'tsp'): Observable<any> {
    return this.http.post<any>(LOGIN_OTP_REQUEST(show), payload);
  }

  /** Verify OTP with roleId (new API) */
  verifyOTPWithRole(payload: VerifyOTPRequest,show: string = 'tsp'): Observable<any> {
    return this.http.post<any>(VERIFY_OTP_REQUEST(show), payload);
  }

  /**
   * Confirm the educator may act on this club/show pair. Returns false for any
   * response that isn't recognisably `true` — an authorization check has to fail
   * closed, and the endpoint's response shape isn't in the published spec.
   */
  validateEducatorShowClubMapping(
    payload: Pick<EducatorShowClubMappingRequest, 'clubUuid' | 'showUuid'>,
    show: string = 'tsp'
  ): Observable<boolean> {
    return this.http
      .post<unknown>(VALIDATE_EDUCATOR_SHOW_CLUB_MAPPING(show), payload)
      .pipe(map((result) => this.readValidationFlag(result)));
  }

  // ===================== Show-admin post-login lookup =====================

  /**
   * Confirm the signed-in show admin is mapped to `payload.showUuid`. Read as
   * text and coerced like the educator check, so a `success: false` envelope
   * (unknown email, inactive account) is a plain denial rather than a thrown
   * error.
   */
  validateStateAdminShow(
    payload: StateAdminShowValidationBody,
    show: string = 'tsp'
  ): Observable<boolean> {
    return this.http
      .post<unknown>(VALIDATE_STATE_ADMIN_SHOW(show), payload)
      .pipe(map((result) => this.readValidationFlag(result)));
  }

  getUserInfo(email: string, show: string = 'tsp'): Observable<unknown> {
    return this.http.post<unknown>(GET_USER_INFO(show), { email });
  }

  /** Keys that may carry the verdict, in the order they're trusted. */
  private static readonly VALIDATION_KEYS = ['data', 'isvalid', 'valid', 'result'];

  /**
   * Pull the verdict out of whatever shape comes back.
   *
   * `success` is deliberately NOT treated as the verdict: on this API it means
   * "the request worked", so a denied mapping still returns `success: true` and
   * trusting it would authorise every 200. Anything unrecognised is a denial.
   */
  private readValidationFlag(value: unknown, depth = 0): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return /^true$/i.test(value.trim());
    }
    if (depth >= 4 || value === null || typeof value !== 'object') {
      return false;
    }

    // The sibling Educator endpoints nest their payload as `data[0][0]`, so a
    // single-element wrapper is unwrapped; anything wider is too ambiguous.
    if (Array.isArray(value)) {
      return value.length === 1 && this.readValidationFlag(value[0], depth + 1);
    }

    // Envelope keys are camelCase but nested payloads are PascalCase, so match
    // case-insensitively rather than guessing which convention applies.
    const entries = new Map(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k.toLowerCase(), v])
    );

    if (entries.get('success') === false) {
      return false;
    }

    const key = CoreService.VALIDATION_KEYS.find((k) => entries.has(k));
    return key ? this.readValidationFlag(entries.get(key), depth + 1) : false;
  }

  // ===================== Org Config Methods =====================

  /** Fetch the public org config for a show abbreviation. */
  fetchOrgConfigs(abbreviation: string): Observable<OrgConfigs> {
    return this.http.get<OrgConfigs>(`${GET_ORG_CONFIGS}?abbreviation=${abbreviation}`);
  }

  setOrgConfig(config: OrgConfigs): void {
    sessionStorage.setItem(this.ORG_CONFIG_KEY, JSON.stringify(config));
  }

  getOrgConfig(): OrgConfigs | null {
    const config = sessionStorage.getItem(this.ORG_CONFIG_KEY);
    return config ? JSON.parse(config) : null;
  }

  clearOrgConfig(): void {
    sessionStorage.removeItem(this.ORG_CONFIG_KEY);
  }

  // ===================== Master Data APIs =====================

  /** Get roles from API */
  getRoles(request: GetRolesRequest, show: string = 'tsp'): Observable<GetRolesResponse> {
    return this.http.post<GetRolesResponse>(GET_ROLES(show), request);
  }

  /** Get shows from API. Rows carry the GUID as `showUuid`, the filter as `showId`. */
  getShows(request: GetShowsRequest, show: string = 'tsp'): Observable<GetShowsResponse> {
    return this.http.post<GetShowsResponse>(GET_SHOWS(show), request);
  }

  /** Competition types configured for a show (Open Show, Junior Show, …).
   *  The show comes from the route, so no payload is sent. */
  getShowTypes(show: string): Observable<GetShowTypesResponse> {
    return this.http.post<GetShowTypesResponse>(GET_SHOW_TYPES(show), {});
  }

  getCounties(show: string = 'tsp'): Observable<GetCountiesResponse> {
    return this.http.post<GetCountiesResponse>(GET_COUNTIES(show), {});
  }
}
